import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Grid,
  CardContent,
  Typography,
  Button,
  CardMedia,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";

import "./CartCard.css"; // Import the external CSS file
import { decrementQty, incrementQty } from "../../sliceCart";

export default function CartCard({ product }) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [imageError, setImageError] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  
  const vRdxDBKernObj = useSelector((state) => state.SliceDB.value.DBKernObj);
  const baseUrl = vRdxDBKernObj?.IMAGE_BASE_URL?.Val;

  return (
    <>
      {!isMobile ? (
        <Card className="card">
          <CardContent sx={{ padding: "8px", paddingBottom: "0 !important" }}>
            <Grid container className="cart-grid">
              <Grid
                item
                xs={2}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {imageError ? (
                  <ImageNotSupportedIcon
                    sx={{
                      width: "65px",
                      height: "65px",
                      color: "#ccc",
                    }}
                  />
                ) : (
                  <CardMedia
                    component="img"
                    image={baseUrl + "Products/" + product?.ArticleNo + ".jpg"}
                    alt={product?.ItmCd}
                    loading="lazy"
                    onError={() => setImageError(true)}
                    sx={{
                      height: 60,
                      fontSize: "0.75rem",
                      objectFit: "contain",
                      backgroundColor: "transparent",
                    }}
                  />
                )}
              </Grid>
              <Grid item xs={3.5}>
                <Typography className="gridItem" component="div">
                  {product?.Name2}
                </Typography>
              </Grid>
              <Grid item xs={2.1}>
                <Typography className="gridItem" component="div">
                  Unit Price{" "}
                  <span style={{ fontWeight: "bold" }}>
                    {product?.VATRSP.toFixed(2)} AED
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={2.1}>
                <Typography className="gridItem" component="div">
                  Total Price{" "}
                  <span style={{ fontWeight: "bold" }}>
                    {product?.NetAmount.toFixed(2)} AED
                  </span>
                </Typography>
              </Grid>
              <Grid item xs={2.3}>
                <div
                  style={{
                    display: "flex",
                    textAlign: "center",
                    justifyContent: "center",
                    alignItems: "center",
                    padding: "3px",
                  }}
                >
                  <Button
                    aria-label="reduce"
                    className="reduceButton"
                    color="error"
                    onClick={() => dispatch(decrementQty(product?.ItmCd))}
                    disabled={product.qty === 0}
                  >
                    <RemoveIcon sx={{ fontSize: 20 }} />
                  </Button>
                  <input readOnly className="input" value={product?.qty} />
                  <Button
                    className="increaseButton"
                    aria-label="increase"
                    onClick={() => dispatch(incrementQty(product?.ItmCd))}
                    color="primary"
                  >
                    <AddIcon sx={{ fontSize: 20 }} />
                  </Button>
                </div>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : (
        <Card className="card">
          <CardContent sx={{ padding: "4px", paddingBottom: "0 !important" }}>
            <Grid container spacing={0.5} alignItems="center">
              {/* Left section for the image */}
              <Grid
                item
                xs={3}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                justifyContent={"center"}
                alignItems={"center"}
              >
                {imageError ? (
                  <ImageNotSupportedIcon
                    sx={{
                      width: "65px",
                      height: "65px",
                      color: "#ccc",
                    }}
                  />
                ) : (
                  <CardMedia
                    component="img"
                    alt={product?.ItmCd}
                    image={baseUrl + "Products/" + product?.ArticleNo + ".jpg"}
                    loading="lazy"
                    onError={() => setImageError(true)}
                    sx={{
                      height: 80,
                      width: 80,
                      objectFit: "contain",
                      backgroundColor: "transparent",
                      borderRadius: "8px",
                    }}
                  />
                )}
              </Grid>
              {/* Right section for product details */}
              <Grid item xs={9}>
                <Grid container direction="column" spacing={0.5}>
                  {/* Product Name */}
                  <Grid item sx={{ display: "flex", justifyContent: "center" }}>
                    <Typography
                      noWrap
                      className="productTitle"
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        fontWeight: "bold",
                        fontSize: "11px",
                      }}
                    >
                      {product?.Name2}
                    </Typography>
                  </Grid>

                  {/* Unit Price and Total Price */}
                  <Grid item sx={{ display: "flex", justifyContent: "center" }}>
                    <Typography className="smText" sx={{ color: "gray" }}>
                      Price : &nbsp;
                      <span className="smText" style={{ fontWeight: "bold" }}>
                        {Number(product?.VATRSP).toFixed(2)} AED &nbsp;
                      </span>
                      | Total : &nbsp;
                      <span className="smText" style={{ fontWeight: "bold" }}>
                        {Number(product?.NetAmount).toFixed(2)} AED
                      </span>
                    </Typography>
                  </Grid>

                  {/* Increment/Decrement Buttons */}
                  <Grid item>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        marginTop: "4px",
                      }}
                    >
                      <Button
                        aria-label="reduce"
                        color="error"
                        sx={{
                          minWidth: "32px",
                          height: "32px",
                          borderRadius: "50%",
                        }}
                        onClick={() => dispatch(decrementQty(product?.ItmCd))}
                        disabled={product?.qty === 0}
                      >
                        <RemoveIcon fontSize="small" />
                      </Button>
                      <input
                        readOnly
                        className="input"
                        value={product?.qty}
                        style={{
                          width: "40px",
                          textAlign: "center",
                          margin: "0 8px",
                          border: "1px solid #ccc",
                          borderRadius: "4px",
                          height: "32px",
                        }}
                      />
                      <Button
                        aria-label="increase"
                        color="primary"
                        sx={{
                          minWidth: "32px",
                          height: "32px",
                          borderRadius: "50%",
                        }}
                        onClick={() => dispatch(incrementQty(product?.ItmCd))}
                      >
                        <AddIcon fontSize="small" />
                      </Button>
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </>
  );
}
